import { useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { toast } from '@core/components';
import {
  readLocalJson,
  removeLocalItem,
  writeLocalJson,
} from '@core/utils/localStorage';
import type { VestmentCategory } from '../../enum/vestmentCategory';
import { getFormValuesCategory } from './helpers';
import { ORDER_ITEM_REGISTRY, emptyOrderItemFormValues } from './registry';
import type { OrderItemFormValues } from './types';

type DraftOrderId = string | number | null | undefined;

const DRAFT_IGNORED_FIELDS = new Set(['id', 'size', 'forCustomer']);

const recentlyClearedDraftKeys = new Set<string>();

function draftKey(
  orderId: string | number,
  category: VestmentCategory
): string {
  return `lv.orderItemDraft.v2:order:${orderId}:category:${category}`;
}

function toDraft(
  category: VestmentCategory,
  values: OrderItemFormValues
): OrderItemFormValues | null {
  const empty = emptyOrderItemFormValues(category);
  const emptyValues = new Map(Object.entries(empty));
  const fields = Object.entries(values).filter(
    ([field, value]) => field !== 'id' && value != null
  );
  const isEmpty = fields.every(
    ([field, value]) =>
      DRAFT_IGNORED_FIELDS.has(field) || value === emptyValues.get(field)
  );
  return isEmpty ? null : { ...empty, ...Object.fromEntries(fields) };
}

function readDraft(
  orderId: string | number,
  category: VestmentCategory
): OrderItemFormValues | null {
  const values = readLocalJson<OrderItemFormValues>(
    draftKey(orderId, category)
  );
  if (!values || getFormValuesCategory(values) !== category) {
    return null;
  }
  const draft = toDraft(category, values);
  // Older drafts stored the design id; the server resolves the code instead.
  return draft && typeof draft.design !== 'object'
    ? { ...draft, design: null }
    : draft;
}

function writeDraft(
  orderId: string | number,
  category: VestmentCategory,
  values: OrderItemFormValues
): void {
  const key = draftKey(orderId, category);
  if (recentlyClearedDraftKeys.has(key)) {
    return;
  }
  const draft = toDraft(category, values);
  if (draft) {
    writeLocalJson(key, draft);
  } else {
    removeLocalItem(key);
  }
}

export function getAddItemInitialValues(
  category: VestmentCategory,
  orderId: DraftOrderId,
  customerId?: string | number | null
): OrderItemFormValues {
  const draft = orderId == null ? null : readDraft(orderId, category);
  if (!draft) {
    return emptyOrderItemFormValues(category, customerId);
  }
  toast(
    `Unsaved ${ORDER_ITEM_REGISTRY[category].label.toLowerCase()} item restored`
  );
  return { ...draft, id: undefined };
}

export function clearOrderItemDraft(
  orderId: DraftOrderId,
  category: VestmentCategory
): void {
  if (orderId == null) {
    return;
  }
  const key = draftKey(orderId, category);
  recentlyClearedDraftKeys.add(key);
  removeLocalItem(key);
}

export function usePersistOrderItemDraft(
  methods: UseFormReturn<OrderItemFormValues>,
  {
    enabled,
    orderId,
    category,
  }: {
    enabled: boolean;
    orderId?: DraftOrderId;
    category?: VestmentCategory;
  }
): void {
  useEffect(() => {
    if (!enabled || orderId == null || category == null) {
      return;
    }
    recentlyClearedDraftKeys.delete(draftKey(orderId, category));

    let timer: ReturnType<typeof setTimeout> | undefined;
    const save = () => {
      writeDraft(orderId, category, methods.getValues());
    };

    const subscription = methods.watch(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(save, 300);
    });

    return () => {
      window.clearTimeout(timer);
      save();
      subscription.unsubscribe();
    };
  }, [enabled, orderId, category, methods]);
}
